import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { QuestionnaireResponse } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Anchor, Badge, Button, Group, Modal, Stack, Table, UnstyledButton } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import { IconEdit, IconNote, IconTrash } from "@tabler/icons-react";
import { format, toDate } from "date-fns-tz";

import { TextWrapper } from "@/components/TextWrapper";
import { BILLING_TABLE_MIN_WIDTH } from "@/encounters/constants/billing-table";
import { CPTCodeActionLabels } from "@/encounters/constants/cpt-code";
import PermissionGate from "@/permission/components/PermissionGate";
import { BILLER_PERMISSION_FLAGS } from "@/permission/constants/practitioner-permissions";
import { useAppDispatch } from "@/store";
import { deleteProcedure, updateProcedure } from "@/store/slices/procedures";
import { QuestionnaireResponseStatus } from "@/utils/enums";
import { ProceduresFormTypeWithId } from "@/zod-schemas/procedures";

import {
  fetchQuestionnaireResponseHistory,
  softDeleteProcedureQuestionnaireResponse,
} from "../services/questionnaire.response";

const EncounterBillingTable = ({
  procedures,
  open,
  handleTargetCptCode,
}: {
  procedures: ProceduresFormTypeWithId[];
  open: () => void;
  handleTargetCptCode: (cptCode: ProceduresFormTypeWithId) => void;
}) => {
  const medplum = useMedplum();
  const [procedureHistories, setProcedureHistories] = useState<
    Record<string, QuestionnaireResponse[]>
  >({});

  useEffect(() => {
    (async () => {
      const proceduresHistory = await fetchQuestionnaireResponseHistory(medplum, procedures);
      setProcedureHistories(proceduresHistory);
    })();
  }, [medplum, procedures]);

  return (
    <Table.ScrollContainer type="native" minWidth={BILLING_TABLE_MIN_WIDTH}>
      <Table verticalSpacing="md">
        <Table.Thead h="48px">
          <Table.Tr bg="gray.0">
            <Table.Th w="15%" fw={600} c="gray.7">
              Procedure Code
            </Table.Th>
            <Table.Th w="50%" fw={600} c="gray.7">
              Code Description
            </Table.Th>
            <Table.Th w="10%" fw={600} c="gray.7">
              Modifiers
            </Table.Th>
            <Table.Th w="10%" fw={600} c="gray.7">
              ICD-10
            </Table.Th>
            <Table.Th w="10%" fw={600} c="gray.7">
              Units
            </Table.Th>
            <Table.Th w="10%" fw={600} c="gray.7">
              Actions
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {procedures?.map((procedure) => (
            <EncounterBillingTableRows
              open={open}
              key={procedure.id}
              procedure={procedure}
              handleTargetCptCode={handleTargetCptCode}
              procedureHistory={procedureHistories[procedure.id]}
            />
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
};

const EncounterBillingTableRows = ({
  procedure,
  open,
  handleTargetCptCode,
  procedureHistory,
}: {
  procedure: ProceduresFormTypeWithId;
  open: () => void;
  handleTargetCptCode: (cptCode: ProceduresFormTypeWithId) => void;
  procedureHistory: QuestionnaireResponse[];
}) => {
  const { id: procedureId, cptCode, cptDiagnosesFormset, exists } = procedure;
  const medplum = useMedplum();
  const dispatch = useAppDispatch();
  const [opened, handlers] = useDisclosure(false);
  const units = procedure?.cptUnits ?? "";
  const status = procedure?.status ?? "";
  const modifiers = Object.values(procedure?.cptModifiersFormset ?? {}).map(
    (modifier) => modifier?.cptModifiersFormsetModifier ?? "",
  );
  const codeDescription = cptCode?.display ?? "";
  const procedureCode = cptCode?.code ?? "";

  const parseDiagnosisFormset = useMemo(() => {
    return Object.values(cptDiagnosesFormset ?? {}).map(
      (diagnosis) => diagnosis?.cptDiagnosesFormsetDiagnosis?.code ?? "",
    );
  }, [cptDiagnosesFormset]);

  const hasMoreThanOneHistory = procedureHistory?.length && procedureHistory?.length > 1;

  const onDeleteProcedure = async () => {
    if (exists) {
      await softDeleteProcedureQuestionnaireResponse(medplum, procedureId);
      dispatch(
        updateProcedure({
          id: procedureId,
          changes: { status: QuestionnaireResponseStatus.Stopped },
        }),
      );
    } else {
      dispatch(deleteProcedure(procedureId));
    }

    handlers.close();
  };

  return (
    <React.Fragment>
      <Modal
        centered
        opened={opened}
        withCloseButton={true}
        onClose={handlers.close}
        styles={{ close: { top: -12 } }}
        title={
          <TextWrapper size="xl" c="burgundy.9" variant="bold">
            Are you sure you want to remove this code?
          </TextWrapper>
        }
      >
        <Stack>
          <TextWrapper>This code will be removed from the encounter.</TextWrapper>
          <Group pt="sm" justify="flex-end" mx={{ base: "xs", xs: 0 }}>
            <Button
              fw="bold"
              variant="outline"
              onClick={handlers.close}
              data-testid="delete-procedure-cancel-button"
            >
              Cancel
            </Button>
            <Button
              fw="bold"
              bg="red.6"
              color="white"
              variant="outline"
              onClick={onDeleteProcedure}
              data-testid="delete-procedure-confirmation-button"
            >
              Yes, Delete Code
            </Button>
          </Group>
        </Stack>
      </Modal>
      <Table.Tr style={{ borderBottom: hasMoreThanOneHistory ? "none" : "" }}>
        <Table.Td>
          <TextWrapper fw={600}>{procedureCode}</TextWrapper>
        </Table.Td>
        <Table.Td>
          <TextWrapper style={{ wordBreak: "break-word" }}>{codeDescription}</TextWrapper>
        </Table.Td>
        <Table.Td>
          <TextWrapper>
            {modifiers?.map((modifier) => (
              <TextWrapper key={modifier}>{modifier}</TextWrapper>
            ))}
          </TextWrapper>
        </Table.Td>
        <Table.Td>
          {parseDiagnosisFormset.map((diagnosis) => (
            <TextWrapper key={diagnosis}>{diagnosis}</TextWrapper>
          ))}
        </Table.Td>
        <Table.Td>{units}</Table.Td>
        <Table.Td>
          {status === QuestionnaireResponseStatus.Stopped ? (
            <Badge
              fw="bold"
              size="md"
              radius="xs"
              color="gray.7"
              variant="outline"
              bd={`2px solid var(--mantine-color-red-6)`}
              leftSection={
                <div
                  style={{
                    height: 6,
                    width: 6,
                    borderRadius: "50%",
                    backgroundColor: "var(--mantine-color-red-5)",
                  }}
                />
              }
            >
              DELETED
            </Badge>
          ) : (
            <Group gap="xs" wrap="nowrap">
              <PermissionGate requiredPermissions={BILLER_PERMISSION_FLAGS}>
                <UnstyledButton
                  onClick={open}
                  aria-label="Add Procedure"
                  data-testid="add-procedure-button"
                >
                  <IconNote />
                </UnstyledButton>
              </PermissionGate>
              <UnstyledButton
                aria-label="Delete Procedure"
                onClick={() => handlers.open()}
                data-testid="delete-procedure-button"
              >
                <IconTrash color="var(--mantine-color-red-6)" />
              </UnstyledButton>
              <PermissionGate requiredPermissions={BILLER_PERMISSION_FLAGS}>
                <UnstyledButton
                  aria-label="Edit Procedure"
                  data-testid="edit-procedure-button"
                  onClick={() => {
                    handleTargetCptCode(procedure);
                    open();
                  }}
                >
                  <IconEdit />
                </UnstyledButton>
              </PermissionGate>
            </Group>
          )}
        </Table.Td>
      </Table.Tr>
      <EncounterBillingTableRowsHistory
        procedureHistory={procedureHistory}
        questionnaireResponseId={procedureId}
      />
    </React.Fragment>
  );
};

const EncounterBillingTableRowsHistory = ({
  procedureHistory,
  questionnaireResponseId,
}: {
  procedureHistory: QuestionnaireResponse[];
  questionnaireResponseId: string;
}) => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();

  if (!procedureHistory?.length || procedureHistory.length === 1) return null;
  const currentVersionNumber = procedureHistory.length.toFixed(1);
  const currentVersionResource = procedureHistory[0];
  const lastUpdatedBy = currentVersionResource.meta?.author?.display;
  const lastUpdated = currentVersionResource.meta?.lastUpdated ?? "";

  const actionLabel =
    currentVersionResource.status === QuestionnaireResponseStatus.Stopped
      ? CPTCodeActionLabels.Deleted
      : CPTCodeActionLabels.Updated;

  return (
    <Table.Tr>
      <Table.Td colSpan={6}>
        <Group p="md" mt="lg" bg="gray.0" justify="center">
          <TextWrapper c="dark.4">{actionLabel}</TextWrapper>
          <TextWrapper fw="bold" c="dark.4" variant="bold">
            {lastUpdatedBy}
          </TextWrapper>
          <TextWrapper c="dark.4">
            on {format(toDate(lastUpdated), "MMMM d, yyyy")} at{" "}
            {format(toDate(lastUpdated), "h:mm a")}
          </TextWrapper>

          <TextWrapper c="dark.4">·</TextWrapper>

          <TextWrapper fw="bold" c="dark.4">
            Current Version v{currentVersionNumber}
          </TextWrapper>

          <TextWrapper lh={1} fw="bold" c="dark.4" style={{ alignSelf: "center" }}>
            |
          </TextWrapper>

          <Anchor
            fw="bold"
            size="sm"
            c="dark.4"
            underline="always"
            data-testid="view-previous-versions-button"
            onClick={() =>
              navigate(`/clients/${clientId}/procedures/version/${questionnaireResponseId}`)
            }
          >
            View Previous Versions
          </Anchor>
        </Group>
      </Table.Td>
    </Table.Tr>
  );
};

export default EncounterBillingTable;
